import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './ProjectCard.css';

// Technology category colors
const CATEGORY_COLORS = {
  frontend: '#3B82F6',
  backend: '#10B981',
  mobile: '#A855F7',
  quantum: '#F59E0B',
  documentation: '#6B7280'
};

const ProjectCard = ({ project, onClick, onFavoriteToggle, isFavorite = false, hideFavorite = false, folders = [], onMoveToFolder, projectContextMenu, onContextMenu }) => {
  const contextMenuButtonRef = useRef(null);
  const contextMenuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Calculate menu position when it opens
  useEffect(() => {
    if (projectContextMenu === project._id && contextMenuButtonRef.current) {
      const buttonRect = contextMenuButtonRef.current.getBoundingClientRect();
      const menuWidth = 200; // Menu width from CSS
      const menuMaxHeight = 400; // Max menu height from CSS
      
      // Position menu directly below the button, aligned to its right edge
      let top = buttonRect.bottom + 4; // Small gap below button
      let left = buttonRect.right - menuWidth; // Align right edge with button right edge
      
      // Adjust if menu would go off-screen
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Calculate actual menu height based on number of folders
      const estimatedMenuHeight = Math.min(
        80 + (folders.length * 42), // header + items
        menuMaxHeight
      );
      
      // If menu extends below viewport, position it above the button
      if (top + estimatedMenuHeight > viewportHeight - 16) {
        top = buttonRect.top - estimatedMenuHeight - 4;
      }
      
      // Ensure menu doesn't go above viewport
      if (top < 8) {
        top = 8;
      }
      
      // If menu extends beyond left edge, shift right
      if (left < 8) {
        left = 8;
      }
      // If menu extends beyond right edge, shift left
      if (left + menuWidth > viewportWidth - 8) {
        left = viewportWidth - menuWidth - 8;
      }
      
      setMenuPosition({ top, left });
    }
  }, [projectContextMenu, project._id, folders.length]);

  // Close menu when clicking outside
  useEffect(() => {
    if (projectContextMenu === project._id) {
      const handleClickOutside = (e) => {
        if (contextMenuRef.current && !contextMenuRef.current.contains(e.target) &&
            contextMenuButtonRef.current && !contextMenuButtonRef.current.contains(e.target)) {
          onContextMenu(null);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [projectContextMenu, project._id, onContextMenu]);

  const getInitials = (name) => {
    if (!name) return 'UN';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return null;
    
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    
    if (days === 0) return `Updated today, ${timeStr}`;
    if (days === 1) return `Updated yesterday, ${timeStr}`;
    if (days < 7) return `Updated ${days} days ago, ${timeStr}`;
    if (days < 30) return `Updated ${Math.floor(days / 7)} weeks ago, ${timeStr}`;
    return `Updated ${date.toLocaleDateString()}, ${timeStr}`;
  };

  const getLastUpdateDate = () => {
    // Try multiple possible date field names from different sources:
    // - visitedAt: from recent_projects collection
    // - updatedAt: standard field
    // - createdAt: standard field  
    // - _updatedAt: MongoDB internal field
    // - _createdAt: MongoDB internal field
    const dateToUse = project.visitedAt || 
                      project.updatedAt || 
                      project._updatedAt ||
                      project.createdAt ||
                      project._createdAt;
    
    const formattedDate = formatDate(dateToUse);
    
    // If no valid date, try creation date fields without "Updated" prefix
    if (!formattedDate) {
      const creationDate = project.createdAt || project._createdAt;
      if (creationDate) {
        const date = new Date(creationDate);
        if (!isNaN(date.getTime())) {
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          return `Created ${date.toLocaleDateString()}, ${timeStr}`;
        }
      }
    }
    
    return formattedDate; // Return null if no date available
  };

  const getTechnologyColor = (technology, categories) => {
    if (!categories) return CATEGORY_COLORS.backend;
    
    // Find technology in the list
    const tech = categories.technologies?.find(t => t.value === technology);
    if (!tech || !tech.category) return CATEGORY_COLORS.backend;
    
    return CATEGORY_COLORS[tech.category] || CATEGORY_COLORS.backend;
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    
    // Simply notify parent component - don't call API directly
    if (onFavoriteToggle) {
      onFavoriteToggle(project._id);
    }
  };

  // Get technologies from project info
  const technologies = project.info?.technologies || [];
  const displayTechnologies = technologies.slice(0, 3);
  const remainingCount = technologies.length - 3;

  // Get additional project information
  const description = project.description || project.info?.description;
  const teamContact = project.info?.teamContact;
  const projectOwner = project.info?.projectOwner || project.createdBy?.name || project.creatorName;
  const environments = project.environments || [];
  const visibility = project.visibility;

  // Card border color from project
  const cardStyle = project.color ? {
    border: `3px solid ${project.color}`,
    boxShadow: `0 0 0 1px ${project.color}20`
  } : {};

  return (
    <div 
      className="project-card"
      onClick={onClick}
      style={cardStyle}
    >
      {/* Favorite star button */}
      {!hideFavorite && (
        <button
          className={`favorite-button ${isFavorite ? 'active' : ''}`}
          onClick={handleFavoriteClick}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? '★' : '☆'}
        </button>
      )}
      {/* Context menu button (3 dots) */}
      {folders.length > 0 && onMoveToFolder && (
        <button
          ref={contextMenuButtonRef}
          className="context-menu-button"
          onClick={(e) => {
            e.stopPropagation();
            onContextMenu(projectContextMenu === project._id ? null : project._id);
          }}
          title="Move to folder"
        >
          ⋯
        </button>
      )}

      {/* Folder context menu - rendered via Portal */}
      {projectContextMenu === project._id && createPortal(
        <div 
          ref={contextMenuRef}
          className="folder-context-menu" 
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="folder-menu-header">Move to folder</div>
          <div 
            className="folder-menu-item"
            onClick={() => {
              onMoveToFolder(project._id, null);
              onContextMenu(null);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 4v8a1 1 0 001 1h10a1 1 0 001-1V4a1 1 0 00-1-1H3a1 1 0 00-1 1z"/>
            </svg>
            <span>No folder</span>
          </div>
          <div className="folder-menu-items-container">
            {folders.map(folder => (
              <div 
                key={folder.folderId}
                className="folder-menu-item"
                onClick={() => {
                  onMoveToFolder(project._id, folder.folderId);
                  onContextMenu(null);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill={folder.color}>
                  <path d="M2 4v8a1 1 0 001 1h10a1 1 0 001-1V6H7L6 4H3a1 1 0 00-1 1z"/>
                </svg>
                <span>{folder.name}</span>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Background image or placeholder */}
      <div className="project-background" style={{
        backgroundImage: project.thumbnail ? `url(${project.thumbnail})` : 'none'
      }}>
        {!project.thumbnail && (
          <div className="project-placeholder">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="24" stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="rgba(255,255,255,0.05)"/>
              <path d="M40 28v24M28 40h24" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="33" cy="35" r="2" fill="rgba(255,255,255,0.3)"/>
              <circle cx="47" cy="35" r="2" fill="rgba(255,255,255,0.3)"/>
              <path d="M32 48c2-3 4-4 8-4s6 1 8 4" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        
        {/* Gradient overlay for text readability */}
        <div className="project-overlay"></div>
      </div>
      
      <div className="project-info">
        <div className="project-creator">
          <div className="creator-avatar">
            {project.createdBy?.avatar ? (
              <img src={project.createdBy.avatar} alt={project.createdBy.name} />
            ) : (
              <span>{getInitials(project.createdBy?.name || project.creatorName || project.info?.projectOwner || 'Unknown')}</span>
            )}
          </div>
          <div className="creator-details">
            <h3 className="project-name">{project.name}</h3>
            {getLastUpdateDate() && (
              <p className="project-date">{getLastUpdateDate()}</p>
            )}
          </div>
        </div>

        {/* Description */}
        {description && (
          <div className="project-description" title={description}>
            {description}
          </div>
        )}

        {/* Technology badges */}
        {displayTechnologies.length > 0 && (
          <div className="project-technologies">
            {displayTechnologies.map((tech, index) => (
              <span
                key={index}
                className="technology-badge"
                style={{ backgroundColor: getTechnologyColor(tech) }}
              >
                {tech}
              </span>
            ))}
            {remainingCount > 0 && (
              <span 
                className="technology-badge more-badge"
                title={`Additional technologies: ${technologies.slice(3).join(', ')}`}
              >
                +{remainingCount}
              </span>
            )}
          </div>
        )}

        {/* Additional metadata */}
        <div className="project-metadata">
          {/* Environments */}
          {environments.length > 0 && (
            <div className="project-metadata-item" title={`Environments: ${environments.map(e => e.label || e.envId).join(', ')}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 2a6 6 0 100 12A6 6 0 008 2zM3 8a5 5 0 1110 0A5 5 0 013 8z"/>
              </svg>
              <span>{environments.length} env{environments.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Visibility */}
          {visibility && (
            <div className="project-metadata-item" title={`Visibility: ${visibility}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                {visibility === 'public' ? (
                  <path d="M8 2c3.5 0 6.5 2.5 7.5 6-1 3.5-4 6-7.5 6S1.5 11.5.5 8C1.5 4.5 4.5 2 8 2zm0 10a4 4 0 100-8 4 4 0 000 8zm0-2a2 2 0 110-4 2 2 0 010 4z"/>
                ) : (
                  <path d="M8 2c-3 0-5.5 1.5-7 4l1.5 1C3.5 5.5 5.5 4 8 4s4.5 1.5 5.5 3l1.5-1C13.5 3.5 11 2 8 2zM2 8l2 2 10-10-1.5-1.5L4 7l-.5-.5L2 8z"/>
                )}
              </svg>
              <span>{visibility}</span>
            </div>
          )}

          {/* Team contact with avatar */}
          {teamContact && (
            <div className="project-metadata-item contact-item" title={`Contact: ${teamContact}`}>
              <div className="contact-avatar">
                <span>{getInitials(teamContact)}</span>
              </div>
              <span className="contact-name">{teamContact.split('@')[0]}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    thumbnail: PropTypes.string,
    color: PropTypes.string,
    description: PropTypes.string,
    visibility: PropTypes.string,
    environments: PropTypes.arrayOf(PropTypes.shape({
      envId: PropTypes.string,
      label: PropTypes.string,
    })),
    createdBy: PropTypes.shape({
      name: PropTypes.string,
      avatar: PropTypes.string,
    }),
    creatorName: PropTypes.string,
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    visitedAt: PropTypes.string,
    info: PropTypes.shape({
      technologies: PropTypes.arrayOf(PropTypes.string),
      projectOwner: PropTypes.string,
      teamContact: PropTypes.string,
      description: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onFavoriteToggle: PropTypes.func,
  isFavorite: PropTypes.bool,
  folders: PropTypes.array,
  onMoveToFolder: PropTypes.func,
  projectContextMenu: PropTypes.string,
  onContextMenu: PropTypes.func,
};

export default ProjectCard;
